/**
 * `atelierStore` — état **transient** de la production en cours (SPEC-UI §1.5).
 *
 * Jeté à la fin : rien n'est persisté, rien n'est autoritatif. Il porte le brouillon (format,
 * sujet, intention evergreen), **les patterns servis par le serveur**, et ce que chaque étape a
 * produit — un score d'affichage et une **timeline** — jusqu'à ce que l'écran de l'Atelier
 * envoie **une seule** action `produce_video` (modèle « one-shot », arbitrage A14).
 *
 * ## Ce qui part vraiment au serveur
 *
 * Les scores ne partent **pas**. Ils sont ici pour l'écran, et pour les marqueurs de La Découpe
 * ⟦A-3⟧. Ce qui part, ce sont les timelines : le serveur régénère le pattern du tour et
 * recalcule les cinq notes lui-même (SPEC-API §6.4). En bac à sable, c'est l'inverse : il n'y a
 * personne pour rescorer, donc ce sont les scores qui font foi et les timelines qui dorment.
 */

import { create } from "zustand";

import type { AtelierScores, AtelierStage, VideoFormat } from "@maxime/shared";

import type {
  EtapeSoumise as EtapeSoumiseClient,
  PatternsServeur,
  SoumissionAtelier,
  TimelineAtelier,
} from "../api/atelier";

import { useRunStore } from "./runStore";

/** Étapes par format (PLAN §4.5) : un short est son propre clip — 3 étapes, pas 5. */
export const ETAPES_SHORT: readonly AtelierStage[] = ["ecriture", "tournage", "montage"];
export const ETAPES_LONGUE: readonly AtelierStage[] = [
  "ecriture",
  "tournage",
  "montage",
  "miniature",
  "promotion",
];

export function etapesDuFormat(format: VideoFormat): readonly AtelierStage[] {
  return format === "short" ? ETAPES_SHORT : ETAPES_LONGUE;
}

export interface Brouillon {
  format: VideoFormat;
  subjectId: string;
  evergreenIntent: boolean;
  extraCardSubjects: string[];
  /**
   * **Le mode jalon** (§9.3, PLAN §4.13) — non `null` ⇒ cette production n'est pas une vidéo
   * ordinaire, c'est la vidéo jalon portant cet id, et l'Atelier enverra `video_jalon` au lieu
   * de `produce_video`.
   *
   * `null`/absent est **l'état neutre** : tout ce qui existait avant continue exactement pareil.
   * C'est le seul champ qui change de destination à l'action ; les cinq étapes, elles, sont les
   * mêmes (`format: "longue"` — un jalon est toujours une longue, cf. `Milestone.format`).
   *
   * ⚠️ Un jalon n'a **pas de sujet** : `subjectId` reste `""`. Ce n'est pas un oubli — le
   * serveur ne passe aucun `alignementSujet` au scoreur pour `milestone_video`
   * (`routes/actions.ts`), donc envoyer un `subject_id` au tirage du pattern ferait jouer au
   * client une partition que le serveur ne régénérerait pas.
   */
  jalonId?: string | null;
  /**
   * ⟦A-11 §3⟧ **La production value** — les Clicks investis AVANT de jouer l'Atelier.
   *
   * Ils relèvent le **plafond de compétence** de cette vidéo, jamais son score : il faut
   * toujours bien jouer pour capter ce qu'ils ont ouvert. C'est ce qui rend l'achat de la
   * victoire impossible, et c'est pour ça que la valeur voyage dans le **brouillon** et pas
   * dans les scores — elle est décidée avant le premier mini-jeu, pas après.
   *
   * `0` est l'état neutre : aucun Click débité, plafond exactement celui d'avant l'avenant.
   */
  productionValue: number;
  /**
   * ⟦A-4 E · M3.30⟧ **Les embauches de cette production** — étape → id du freelance retenu
   * au marché. Une étape présente ici n'est PAS jouée : l'Atelier la saute, et la soumission
   * l'envoie déléguée (`{ delegated: true, freelance_id }` en compte ; `scores.delegated` +
   * `scores.freelances` en bac à sable). Le moteur facture à la publication, aux prix que le
   * marché a affichés.
   *
   * Absent/vide = l'état d'avant M3.30 : tout se joue à la main, au caractère près.
   */
  embauches?: Partial<Record<AtelierStage, string>>;
}

export type PhaseEtape = "intro" | "jeu" | "score";

/** Où en est la récupération des patterns du tour (mode compte uniquement). */
export type EtatPatterns = "inutile" | "chargement" | "prets" | "erreur";

interface AtelierStore {
  brouillon: Brouillon | null;
  /** Index de l'étape courante dans `etapesDuFormat(format)`. */
  index: number;
  phase: PhaseEtape;
  /** Score d'**affichage** de chaque étape. `null` = le client ne sait pas noter (La Découpe). */
  scores: Partial<Record<AtelierStage, number | null>>;
  /** Les gestes joués, étape par étape — c'est **ça** que `produce_video` emporte. */
  timelines: Partial<Record<AtelierStage, TimelineAtelier>>;
  /** Les 5 patterns du tour, servis par `GET /atelier-pattern`. `null` en bac à sable. */
  patterns: PatternsServeur | null;
  etatPatterns: EtatPatterns;
  erreurPatterns: string | null;
  demarrer(brouillon: Brouillon): void;
  /**
   * ⟦A-83 · SPEC-UI §3.3/§5.6⟧ **Confier l'étape courante depuis son intro** — le bouton
   * « Déléguer ». Même chemin que le plan du marché (`NouvelleVideo` → `brouillon.embauches`) :
   * l'étape entre dans `embauches` avec QUI, l'Atelier la saute, la soumission l'envoie
   * `{ delegated: true, freelance_id }` et le moteur facture à la publication, au prix que le
   * marché a affiché. Pas un deuxième chemin d'embauche : une deuxième porte sur le même.
   */
  embaucher(stage: AtelierStage, freelanceId: string): void;
  setPhase(phase: PhaseEtape): void;
  poserPatterns(patterns: PatternsServeur | null, etat: EtatPatterns, erreur?: string): void;
  /** Remplace le seul pattern de Promotion — voir `Atelier.tsx` (marqueurs ⟦A-3⟧). */
  rafraichirPromotion(patterns: PatternsServeur): void;
  poserScore(stage: AtelierStage, score: number | null, timeline: TimelineAtelier): void;
  etapeSuivante(): void;
  /** ⟦A-83 · SPEC-UI §1.7/§3.3⟧ Reprendre une production sauvegardée — voir `SauvegardeAtelier`. */
  restaurer(sauvegarde: SauvegardeAtelier): void;
  reset(): void;
}

/**
 * ⟦A-83 · SPEC-UI §1.7 · §3.3⟧ **Le brouillon qui survit à l'onglet.**
 *
 * La spec voulait un brouillon serveur (« quitter l'Atelier laisse la production en brouillon
 * côté serveur, `production` non nul ») ; l'arbitrage A14 a retenu le one-shot, et le backlog
 * disait la conséquence : « le brouillon devient local (perdu au rechargement) ». Il est
 * désormais local **et sauvegardé** : ce store s'écrit dans `localStorage` à chaque changement,
 * et `RepriseAtelier` propose de reprendre au boot (dialogue maison, §1.7).
 *
 * Ce qui rend la reprise SÛRE, sans toucher au moteur ni à l'API :
 *  - elle est **liée à la run ET au tour** (`runId`, `turn`). Le pattern d'Atelier est une
 *    fonction pure de `(seed, turn, étape)` : tant que le tour n'a pas bougé, la partition
 *    reprise est exactement celle que le serveur rejouera — et l'Atelier la redemande de toute
 *    façon au montage. Si le tour a avancé (un autre onglet a joué), la sauvegarde est jetée :
 *    reprendre noterait des gestes sur une autre partition ;
 *  - une étape **en cours de geste** (`phase: "jeu"`) n'est jamais reprise au milieu : sa
 *    timeline vit dans le composant, pas dans le store. On reprend à l'intro de cette étape ;
 *    les étapes déjà notées gardent leur score et leur timeline ;
 *  - rien n'est sauvegardé qui ne soit déjà dans le store : aucune donnée nouvelle, aucune
 *    règle. `reset()` (publication, « Jeter le projet ») efface.
 */
export interface SauvegardeAtelier {
  version: 1;
  runId: string;
  turn: number;
  brouillon: Brouillon;
  index: number;
  phase: PhaseEtape;
  scores: Partial<Record<AtelierStage, number | null>>;
  timelines: Partial<Record<AtelierStage, TimelineAtelier>>;
  patterns: PatternsServeur | null;
}

export const CLE_BROUILLON = "maxime.atelier.brouillon.v1";

/** Le strict nécessaire de `Storage` — pour tester sans DOM. */
export interface StockageBrouillon {
  getItem(cle: string): string | null;
  setItem(cle: string, valeur: string): void;
  removeItem(cle: string): void;
}

function stockageParDefaut(): StockageBrouillon | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

export function lireSauvegarde(stockage = stockageParDefaut()): SauvegardeAtelier | null {
  if (!stockage) return null;
  try {
    const brut = stockage.getItem(CLE_BROUILLON);
    if (!brut) return null;
    const s = JSON.parse(brut) as Partial<SauvegardeAtelier>;
    if (s.version !== 1 || !s.brouillon || typeof s.runId !== "string" || typeof s.turn !== "number") {
      stockage.removeItem(CLE_BROUILLON);
      return null;
    }
    return s as SauvegardeAtelier;
  } catch {
    return null;
  }
}

export function effacerSauvegarde(stockage = stockageParDefaut()): void {
  try {
    stockage?.removeItem(CLE_BROUILLON);
  } catch {
    // Pas de stockage : rien à effacer.
  }
}

function ecrireSauvegarde(s: SauvegardeAtelier, stockage = stockageParDefaut()): void {
  try {
    stockage?.setItem(CLE_BROUILLON, JSON.stringify(s));
  } catch {
    // Mode privé ou quota : la production continue, elle ne survivra juste pas à l'onglet.
  }
}

/**
 * La sauvegarde vaut-elle pour CETTE run, à CE tour ? `null` sinon — et la distinction entre
 * « autre run » et « même run, tour avancé » est rendue pour que l'écran puisse la dire.
 */
export function verdictReprise(
  sauvegarde: SauvegardeAtelier | null,
  run: { runId: string; state: { turn: number } } | null,
): "reprenable" | "tour_avance" | "autre_run" | "aucune" {
  if (!sauvegarde || !run) return "aucune";
  if (sauvegarde.runId !== run.runId) return "autre_run";
  if (sauvegarde.turn !== run.state.turn) return "tour_avance";
  return "reprenable";
}

/** Ce qui se restaure d'une sauvegarde : tout, sauf un geste en cours (jeu → intro). */
export function etatRestaure(s: SauvegardeAtelier): Pick<
  AtelierStore,
  "brouillon" | "index" | "phase" | "scores" | "timelines" | "patterns" | "etatPatterns" | "erreurPatterns"
> {
  return {
    brouillon: s.brouillon,
    index: s.index,
    phase: s.phase === "jeu" ? "intro" : s.phase,
    scores: s.scores,
    timelines: s.timelines,
    patterns: s.patterns,
    // « prets » avec des patterns : l'Atelier les redemande quand même au montage (même tour ⇒
    // même partition), et c'est cette redemande qui vérifie que le tour n'a pas bougé.
    etatPatterns: s.patterns ? "prets" : "inutile",
    erreurPatterns: null,
  };
}

export const useAtelierStore = create<AtelierStore>((set, get) => ({
  brouillon: null,
  index: 0,
  phase: "intro",
  scores: {},
  timelines: {},
  patterns: null,
  etatPatterns: "inutile",
  erreurPatterns: null,

  demarrer(brouillon) {
    set({
      brouillon,
      index: 0,
      phase: "intro",
      scores: {},
      timelines: {},
      patterns: null,
      etatPatterns: "inutile",
      erreurPatterns: null,
    });
  },

  embaucher(stage, freelanceId) {
    const brouillon = get().brouillon;
    if (!brouillon) return;
    // L'étape confiée sort des étapes JOUÉES : l'index ne bouge pas, c'est la suivante qui
    // prend sa place à l'intro. Un score ou une timeline qu'elle aurait porté n'existe pas —
    // on ne délègue qu'à l'intro, avant le geste — mais on nettoie quand même : une étape
    // confiée n'a pas de geste, et un geste qui fuirait serait noté par le serveur.
    const { [stage]: _score, ...scores } = get().scores;
    const { [stage]: _timeline, ...timelines } = get().timelines;
    set({
      brouillon: { ...brouillon, embauches: { ...(brouillon.embauches ?? {}), [stage]: freelanceId } },
      scores,
      timelines,
      phase: "intro",
    });
  },

  setPhase(phase) {
    set({ phase });
  },

  poserPatterns(patterns, etat, erreur) {
    set({ patterns, etatPatterns: etat, erreurPatterns: erreur ?? null });
  },

  rafraichirPromotion(patterns) {
    const courant = get().patterns;
    if (!courant) return;
    set({
      patterns: {
        ...courant,
        patterns: { ...courant.patterns, promotion: patterns.patterns.promotion },
      },
    });
  },

  poserScore(stage, score, timeline) {
    set({
      scores: { ...get().scores, [stage]: score === null ? null : Math.round(score) },
      timelines: { ...get().timelines, [stage]: timeline },
      phase: "score",
    });
  },

  etapeSuivante() {
    set({ index: get().index + 1, phase: "intro" });
  },

  restaurer(sauvegarde) {
    set(etatRestaure(sauvegarde));
  },

  reset() {
    set({
      brouillon: null,
      index: 0,
      phase: "intro",
      scores: {},
      timelines: {},
      patterns: null,
      etatPatterns: "inutile",
      erreurPatterns: null,
    });
  },
}));

/**
 * ⟦A-83⟧ La sauvegarde suit le store : à chaque changement, le brouillon s'écrit avec la run et le
 * tour du moment ; sans brouillon, la clé disparaît. L'identité de run vient du miroir
 * (`runStore`) — l'Atelier ne la connaît pas autrement, et c'est la seule chose qu'on y lit.
 */
useAtelierStore.subscribe((s) => {
  if (!s.brouillon) {
    effacerSauvegarde();
    return;
  }
  const run = useRunStore.getState().run;
  if (!run) return;
  ecrireSauvegarde({
    version: 1,
    runId: run.runId,
    turn: run.state.turn,
    brouillon: s.brouillon,
    index: s.index,
    phase: s.phase,
    scores: s.scores,
    timelines: s.timelines,
    patterns: s.patterns,
  });
});

/**
 * Convertit les scores saisis en `AtelierScores` du moteur (§1.2). Un short laisse
 * `miniature` et `promotion` à `null` : c'est la spec, pas un raccourci.
 *
 * ⚠️ **Mode compte : ces scores sont envoyés, et le serveur ne les lit pas.** Son union d'action
 * n'a même pas de champ pour les porter (`server/src/routes/actions.ts`) — la traduction
 * `versActionApi` les laisse tomber et met les timelines à la place. Les calculer quand même,
 * c'est ce qui permet à l'écran de récap d'exister dans les deux modes avec un seul code.
 */
export function scoresPourMoteur(
  format: VideoFormat,
  scores: Partial<Record<AtelierStage, number | null>>,
  embauches: Partial<Record<AtelierStage, string>> = {},
): AtelierScores {
  const lire = (stage: AtelierStage) => Math.max(0, Math.min(100, Math.round(scores[stage] ?? 0)));
  // ⟦A-4 E · M3.30⟧ Les étapes embauchées partent déléguées, avec QUI : c'est ce champ que
  // `offreRetenue` lit, et sans lui le moteur retomberait sur le moins cher — pas sur la
  // personne que le joueur a choisie au marché. Score 0 sur ces étapes : la livraison du
  // freelance le remplace à la publication, et un 0 qui fuirait est un bug qu'on veut voir.
  const delegated = etapesDuFormat(format).filter((s) => embauches[s] !== undefined);
  const freelances: Partial<Record<AtelierStage, string>> = {};
  for (const s of delegated) freelances[s] = embauches[s]!;
  return {
    ecriture: delegated.includes("ecriture") ? 0 : lire("ecriture"),
    tournage: delegated.includes("tournage") ? 0 : lire("tournage"),
    montage: delegated.includes("montage") ? 0 : lire("montage"),
    miniature: format === "short" ? null : delegated.includes("miniature") ? 0 : lire("miniature"),
    promotion: format === "short" ? null : delegated.includes("promotion") ? 0 : lire("promotion"),
    delegated: [...delegated],
    ...(delegated.length > 0 ? { freelances } : {}),
  };
}

/**
 * La soumission d'Atelier de §6.3, assemblée depuis les timelines jouées.
 *
 * Rend `null` s'il manque une étape : on ne complète **jamais** un trou par une timeline vide,
 * ce serait envoyer au serveur un geste qui n'a pas eu lieu. Un short n'a ni miniature ni
 * promotion — les inclure ferait un `422 atelier_shape`.
 */
export function soumissionAtelier(
  format: VideoFormat,
  timelines: Partial<Record<AtelierStage, TimelineAtelier>>,
  embauches: Partial<Record<AtelierStage, string>> = {},
  /** ⟦A-83⟧ Les gestes ont été joués sur la variante posée (SPEC-UI §7.4). */
  reducedMotion = false,
): SoumissionAtelier | null {
  const etapes = etapesDuFormat(format);
  // ⟦A-4 E · M3.30⟧ Une étape embauchée n'a pas de geste — elle part `{ delegated: true,
  // freelance_id }` : le serveur valide l'embauche (`embaucheValide`) et substitue la
  // livraison. Les étapes JOUÉES, elles, exigent toujours leur timeline : on ne complète
  // jamais un trou par une timeline vide.
  const etape = (stage: AtelierStage): EtapeSoumiseClient | null => {
    const id = embauches[stage];
    if (id !== undefined) return { delegated: true, freelance_id: id };
    return timelines[stage] ? { timeline: timelines[stage] } : null;
  };
  for (const stage of etapes) if (etape(stage) === null) return null;
  const soumission: SoumissionAtelier = {
    ecriture: etape("ecriture")!,
    tournage: etape("tournage")!,
    montage: etape("montage")!,
  };
  if (format !== "short") {
    soumission.miniature = etape("miniature")!;
    soumission.promotion = etape("promotion")!;
  }
  // Jamais `false` : absent est l'état neutre, et le serveur régénère alors le pattern d'avant.
  if (reducedMotion) soumission.reduced_motion = true;
  return soumission;
}
